import React, { useState, useEffect } from 'react';
import { C, fmt } from '../tokens.js';
import { D } from '../mock-data.js';
import { Card, Btn, BkHdr, Badge, Chip, SPill, SHead, Toggle, Divider } from '../components/ui.jsx';
import { createSupplierInvite, listSupplierInvites, cancelSupplierInvite } from '../api/supplierInvites.js';

const VENDOR = {
  id: 'g1',
  name: 'Nandini Dairy',
  type: 'Dairy & Grocery',
  rating: 4.8,
  reviews: 320,
  dist: 1.2,
  hours: '6 AM – 9 PM',
  certified: true,
  em: '🥛',
  bg: '#dbeafe',
};

const TODAY_ORDERS = [
  { id:'V001', customer:'Priya S.', items:'Full Cream Milk 2L + Curd 500g', time:'6:30 AM', status:'delivered', amt:10030, phone:'+91 98765 43210' },
  { id:'V002', customer:'Rahul M.', items:'Toned Milk 1L', time:'6:45 AM', status:'delivered', amt:5200, phone:'+91 98765 43211' },
  { id:'V003', customer:'Anitha K.', items:'Full Cream Milk 500ml', time:'7:00 AM', status:'out_for_delivery', amt:3100, phone:'+91 98765 43212' },
  { id:'V004', customer:'Suresh P.', items:'Paneer 200g', time:'7:15 AM', status:'confirmed', amt:8500, phone:'+91 98765 43213' },
  { id:'V005', customer:'Meena R.', items:'Curd 500g', time:'7:30 AM', status:'confirmed', amt:3800, phone:'+91 98765 43214' },
];

const WEEK_DATA = [
  {day:'Mon', orders:42, rev:34200},
  {day:'Tue', orders:38, rev:31000},
  {day:'Wed', orders:55, rev:45100},
  {day:'Thu', orders:47, rev:38400},
  {day:'Fri', orders:61, rev:50300},
  {day:'Sat', orders:72, rev:59000},
  {day:'Sun', orders:47, rev:38400},
];

const PRODUCTS = [
  { id:'p101', name:'Full Cream Milk 500ml', price:3100, stock:true,  orders:124, em:'🥛' },
  { id:'p102', name:'Toned Milk 500ml',      price:2700, stock:true,  orders:98,  em:'🥛' },
  { id:'p103', name:'Curd 500g',             price:3800, stock:true,  orders:76,  em:'🍶' },
  { id:'p104', name:'Paneer 200g',           price:8500, stock:false, orders:20,  em:'🧀' },
];

export const VendorDashboard = ({ goBack }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [orders, setOrders] = useState(TODAY_ORDERS);
  const [products, setProducts] = useState(PRODUCTS);
  const [orderFilter, setOrderFilter] = useState('all');
  const [slotsOpen, setSlotsOpen] = useState(true);
  const [storeOpen, setStoreOpen] = useState(true);

  const todayRev = orders.reduce((a,o)=>a+o.amt,0);
  const delivered = orders.filter(o=>o.status==='delivered').length;
  const pending = orders.filter(o=>o.status==='confirmed').length;
  const maxOrders = Math.max(...WEEK_DATA.map(d=>d.orders));

  const markReady = (id) => setOrders(os=>os.map(o=>o.id===id?{...o,status:'out_for_delivery'}:o));
  const markDelivered = (id) => setOrders(os=>os.map(o=>o.id===id?{...o,status:'delivered'}:o));
  const toggleStock = (id) => setProducts(ps=>ps.map(p=>p.id===id?{...p,stock:!p.stock}:p));

  const filteredOrders = orderFilter==='all' ? orders : orders.filter(o=>o.status===orderFilter);

  const TABS = [{id:'overview',l:'Overview'},{id:'orders',l:`Orders (${pending})`},{id:'products',l:'Products'},{id:'invites',l:'Invites'},{id:'settings',l:'Settings'}];

  // Invites tab state
  const vendorProducts = D.branches.grocery.find((b) => b.id === VENDOR.id)?.products || [];
  const [inviteForm, setInviteForm] = useState({ phone:'', name:'', kind:'store', productId:'', variantLabel:'', bundleItems:[], deliveryWindow:'6–8 AM' });
  const [invites, setInvites] = useState([]);
  const [generated, setGenerated] = useState(null);  // { token, url, expiresAt }
  const [creating, setCreating] = useState(false);
  const [copiedTimer, setCopiedTimer] = useState(false);

  useEffect(() => {
    if (activeTab !== 'invites') return;
    listSupplierInvites({ branchId: VENDOR.id }).then(setInvites).catch(() => setInvites([]));
  }, [activeTab]);

  const refreshInvites = () => listSupplierInvites({ branchId: VENDOR.id }).then(setInvites).catch(() => {});

  const buildTarget = () => {
    if (inviteForm.kind === 'store') return { kind: 'store' };
    if (inviteForm.kind === 'product') {
      const p = vendorProducts.find((x) => x.id === inviteForm.productId);
      return { kind: 'product', productId: inviteForm.productId, variantLabel: inviteForm.variantLabel || p?.variants?.[0]?.l };
    }
    return {
      kind: 'bundle',
      items: inviteForm.bundleItems.map((it) => ({ productId: it.productId, variantLabel: it.variantLabel, qty: it.qty, cadence: it.cadence })),
      deliveryWindow: inviteForm.deliveryWindow,
    };
  };

  const inviteFormValid = () => {
    if (!/^\d{10}$/.test(inviteForm.phone)) return false;
    if (inviteForm.kind === 'product' && !inviteForm.productId) return false;
    if (inviteForm.kind === 'bundle' && inviteForm.bundleItems.length === 0) return false;
    return true;
  };

  const handleGenerate = async () => {
    if (!inviteFormValid()) return;
    setCreating(true);
    try {
      const res = await createSupplierInvite({ branchId: VENDOR.id, customerPhone: inviteForm.phone, customerName: inviteForm.name || null, target: buildTarget() });
      setGenerated(res);
      refreshInvites();
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = (url) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => { setCopiedTimer(true); setTimeout(() => setCopiedTimer(false), 1500); }).catch(() => {});
    }
  };

  const handleWhatsApp = (url, phone, name) => {
    const msg = encodeURIComponent(`Hi${name ? ' ' + name : ''}, here's your link from ${VENDOR.name}: ${url}`);
    window.open(`https://wa.me/91${phone}?text=${msg}`, '_blank');
  };

  const handleResetForm = () => {
    setGenerated(null);
    setInviteForm({ phone:'', name:'', kind:'store', productId:'', variantLabel:'', bundleItems:[], deliveryWindow:'6–8 AM' });
  };

  const handleCancelInvite = async (token) => {
    if (!window.confirm('Cancel this invite? The customer will see "Invite no longer active" if they open the URL.')) return;
    await cancelSupplierInvite(token);
    refreshInvites();
  };

  const addBundleItem = () => {
    const first = vendorProducts[0];
    if (!first) return;
    setInviteForm((f) => ({ ...f, bundleItems: [...f.bundleItems, { productId: first.id, variantLabel: first.variants[0].l, qty: 1, cadence: 'Daily' }] }));
  };

  const updateBundleItem = (idx, patch) => {
    setInviteForm((f) => ({ ...f, bundleItems: f.bundleItems.map((it, i) => i === idx ? { ...it, ...patch } : it) }));
  };

  const removeBundleItem = (idx) => {
    setInviteForm((f) => ({ ...f, bundleItems: f.bundleItems.filter((_, i) => i !== idx) }));
  };

  // Invite analytics
  const paidInvites = invites.filter((i) => i.status === 'paid');
  const invitesRevenueP = paidInvites.reduce((a, i) => a + (i.revenueP || 0), 0);
  const conversionPct = invites.length > 0 ? Math.round((paidInvites.length / invites.length) * 100) : 0;

  const statusPillFor = (s) => {
    const m = { pending_otp:['#fef3c7','#d97706','Pending OTP'], pending_payment:['#dbeafe','#2563eb','Awaiting payment'], paid:['#dcfce7','#16a34a','Paid'], expired:['#f1f5f9','#64748b','Expired'], cancelled:['#fee2e2','#dc2626','Cancelled'] };
    const [bg, color, l] = m[s] || ['#f1f5f9','#64748b', s];
    return <Badge bg={bg} color={color} sm>{l}</Badge>;
  };

  const targetSummary = (inv) => {
    const t = inv.target;
    if (t.kind === 'store') return '🏬 Store visit';
    if (t.kind === 'product') {
      const p = vendorProducts.find((x) => x.id === t.productId);
      return `📦 ${p?.name || 'Product'}${t.variantLabel ? ' · ' + t.variantLabel : ''}`;
    }
    if (t.kind === 'bundle') return `📅 Bundle · ${t.items?.length || 0} items`;
    return '—';
  };

  const maskPhone = (phone) => phone ? `+91 ••••• ${phone.slice(-5)}` : '';

  return (
    <div className="fade-in" style={{flex:1, display:'flex', flexDirection:'column'}}>
      {/* Vendor header */}
      <div style={{padding:'16px 20px', background:'linear-gradient(135deg,#1c1917,#292524)', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0}}>
        <div style={{display:'flex', gap:12, alignItems:'center'}}>
          <div style={{width:46, height:46, borderRadius:14, background:'#e6f7fb', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, flexShrink:0}}>🥛</div>
          <div>
            <div style={{display:'flex', gap:6, alignItems:'center', marginBottom:2}}>
              <p style={{fontSize:16, fontWeight:800, color:C.white}}>{VENDOR.name}</p>
              <Badge bg="#e6f7fb" color={C.primary} sm>Vendor</Badge>
              {storeOpen ? <Badge bg="#dcfce7" color="#15803d" sm>Open</Badge> : <Badge bg="#fee2e2" color="#dc2626" sm>Closed</Badge>}
            </div>
            <p style={{fontSize:12, color:'rgba(255,255,255,.5)'}}>Bandra West · {VENDOR.type}</p>
          </div>
        </div>
        <button onClick={goBack} style={{padding:'8px 12px', borderRadius:10, border:'1px solid rgba(255,255,255,.2)', background:'rgba(255,255,255,.1)', color:C.white, fontSize:12, fontWeight:700, cursor:'pointer'}}>← Consumer</button>
      </div>

      {/* Tabs */}
      <div style={{display:'flex', background:'#f5f5f4', padding:4, gap:2, flexShrink:0}}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setActiveTab(t.id)} style={{flex:1, padding:'8px 4px', borderRadius:10, border:'none', background:activeTab===t.id?C.white:'transparent', color:activeTab===t.id?C.primary:C.textMed, fontSize:11, fontWeight:700, cursor:'pointer', boxShadow:activeTab===t.id?'0 1px 4px rgba(0,0,0,.08)':'none', transition:'all .18s', whiteSpace:'nowrap'}}>
            {t.l}
          </button>
        ))}
      </div>

      <div style={{flex:1, overflowY:'auto', padding:'16px 20px', display:'flex', flexDirection:'column', gap:16}}>

        {/* ── Overview ── */}
        {activeTab==='overview' && <>
          {/* Today stats */}
          <SHead title="Today's overview"/>
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:10}}>
            {[
              {l:"Orders", v:orders.length, em:'📦', c:C.primary, bg:'#e6f7fb'},
              {l:'Revenue', v:`₹${(todayRev/100).toLocaleString('en-IN',{maximumFractionDigits:0})}`, em:'💰', c:C.success, bg:'#dcfce7'},
              {l:'Delivered', v:delivered, em:'✅', c:C.success, bg:'#f0fdf4'},
              {l:'Pending', v:pending, em:'⏳', c:C.warning, bg:'#fef3c7'},
            ].map((m,i)=>(
              <div key={i} style={{padding:'16px', background:m.bg, borderRadius:18, display:'flex', gap:10, alignItems:'center'}}>
                <span style={{fontSize:26}}>{m.em}</span>
                <div><p style={{fontSize:20, fontWeight:800, color:m.c}}>{m.v}</p><p style={{fontSize:11, fontWeight:600, color:C.textMed}}>{m.l}</p></div>
              </div>
            ))}
          </div>

          {/* Weekly bar chart */}
          <Card>
            <SHead title="This week"/>
            <div style={{display:'flex', alignItems:'flex-end', gap:8, height:100, marginBottom:8}}>
              {WEEK_DATA.map((d,i)=>{
                const isToday = i===3;
                const h = Math.round((d.orders/maxOrders)*100);
                return (
                  <div key={i} style={{flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:4}}>
                    <span style={{fontSize:9, fontWeight:700, color:isToday?C.primary:C.textMed}}>{d.orders}</span>
                    <div style={{width:'100%', height:`${h}%`, background:isToday?C.primary:'#d4f1f7', borderRadius:'4px 4px 0 0', minHeight:4, transition:'height .5s'}}/>
                    <span style={{fontSize:9, color:isToday?C.primary:C.textMed, fontWeight:isToday?700:400}}>{d.day}</span>
                  </div>
                );
              })}
            </div>
            <Divider my={10}/>
            <div style={{display:'flex', justifyContent:'space-between'}}>
              <span style={{fontSize:13, color:C.textMed}}>Weekly revenue</span>
              <span style={{fontSize:14, fontWeight:800, color:C.primary}}>₹{(WEEK_DATA.reduce((a,d)=>a+d.rev,0)/100).toLocaleString('en-IN',{maximumFractionDigits:0})}</span>
            </div>
          </Card>

          {/* Top products */}
          <Card>
            <SHead title="Top products"/>
            {PRODUCTS.map((p,i)=>{
              const maxO = Math.max(...PRODUCTS.map(x=>x.orders));
              return (
                <div key={i} style={{marginBottom:i<PRODUCTS.length-1?14:0}}>
                  <div style={{display:'flex', justifyContent:'space-between', marginBottom:5}}>
                    <div style={{display:'flex', gap:8, alignItems:'center'}}>
                      <span style={{fontSize:16}}>{p.em}</span>
                      <span style={{fontSize:13, fontWeight:600, color:C.text}}>{p.name}</span>
                    </div>
                    <span style={{fontSize:12, fontWeight:700, color:C.primary}}>{p.orders} orders</span>
                  </div>
                  <div style={{height:6, background:'#f5f5f4', borderRadius:3, overflow:'hidden'}}>
                    <div style={{width:`${(p.orders/maxO)*100}%`, height:'100%', background:`linear-gradient(90deg,${C.primary},${C.accent})`, borderRadius:3}}/>
                  </div>
                </div>
              );
            })}
          </Card>
        </>}

        {/* ── Orders ── */}
        {activeTab==='orders' && <>
          <div style={{display:'flex', gap:8, overflowX:'auto', scrollbarWidth:'none'}}>
            {['all','confirmed','out_for_delivery','delivered'].map(f=>(
              <Chip key={f} active={orderFilter===f} onClick={()=>setOrderFilter(f)}>
                {f==='all'?'All':f==='out_for_delivery'?'On the way':f.charAt(0).toUpperCase()+f.slice(1)}
              </Chip>
            ))}
          </div>
          {filteredOrders.map(o=>(
            <Card key={o.id} style={{padding:'14px 16px'}}>
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8}}>
                <div>
                  <p style={{fontSize:14, fontWeight:700, color:C.text}}>{o.customer}</p>
                  <p style={{fontSize:11, color:C.textMed}}>#{o.id} · {o.time}</p>
                </div>
                <SPill status={o.status}/>
              </div>
              <p style={{fontSize:12, color:C.textMed, marginBottom:10}}>{o.items}</p>
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                <span style={{fontSize:14, fontWeight:800, color:C.primary}}>{fmt(o.amt)}</span>
                <div style={{display:'flex', gap:8}}>
                  {o.status==='confirmed' && (
                    <button onClick={()=>markReady(o.id)} style={{padding:'7px 14px', borderRadius:10, border:'none', background:C.primary, color:C.white, fontSize:12, fontWeight:700, cursor:'pointer'}}>Mark ready</button>
                  )}
                  {o.status==='out_for_delivery' && (
                    <button onClick={()=>markDelivered(o.id)} style={{padding:'7px 14px', borderRadius:10, border:'none', background:C.success, color:C.white, fontSize:12, fontWeight:700, cursor:'pointer'}}>Delivered ✓</button>
                  )}
                  <a href={`tel:${o.phone}`} style={{padding:'7px 14px', borderRadius:10, border:`1px solid ${C.border}`, background:C.white, color:C.text, fontSize:12, fontWeight:700, cursor:'pointer', textDecoration:'none'}}>📞</a>
                </div>
              </div>
            </Card>
          ))}
          {filteredOrders.length===0 && (
            <div style={{textAlign:'center', padding:'40px 20px'}}>
              <div style={{fontSize:48, marginBottom:12}}>📋</div>
              <p style={{fontSize:14, fontWeight:700, color:C.text}}>No {orderFilter==='all'?'':orderFilter} orders</p>
            </div>
          )}
        </>}

        {/* ── Products ── */}
        {activeTab==='products' && <>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4}}>
            <p style={{fontSize:13, color:C.textMed}}>{products.filter(p=>p.stock).length} of {products.length} in stock</p>
            <button style={{padding:'8px 14px', borderRadius:10, border:`1.5px solid ${C.primary}`, background:'#e6f7fb', color:C.primary, fontSize:12, fontWeight:700, cursor:'pointer'}}>+ Add product</button>
          </div>
          {products.map(p=>(
            <Card key={p.id} style={{padding:'14px 16px'}}>
              <div style={{display:'flex', gap:12, alignItems:'center'}}>
                <div style={{width:48, height:48, borderRadius:13, background:p.stock?'#e6f7fb':'#f5f5f4', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, flexShrink:0}}>{p.em}</div>
                <div style={{flex:1}}>
                  <p style={{fontSize:14, fontWeight:700, color:C.text}}>{p.name}</p>
                  <p style={{fontSize:12, color:C.textMed}}>{fmt(p.price)} · {p.orders} orders this week</p>
                </div>
                <div style={{display:'flex', flexDirection:'column', alignItems:'flex-end', gap:8}}>
                  <Toggle val={p.stock} onChange={()=>toggleStock(p.id)}/>
                  <span style={{fontSize:10, fontWeight:600, color:p.stock?C.success:C.error}}>{p.stock?'In stock':'Out of stock'}</span>
                </div>
              </div>
            </Card>
          ))}
        </>}

        {/* ── Settings ── */}
        {activeTab==='settings' && <>
          <Card>
            <SHead title="Store status"/>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 0', borderBottom:`1px solid ${C.border}`}}>
              <div><p style={{fontSize:14, fontWeight:700, color:C.text}}>Store open</p><p style={{fontSize:12, color:C.textMed}}>Visible to customers, accepting orders</p></div>
              <Toggle val={storeOpen} onChange={setStoreOpen}/>
            </div>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 0'}}>
              <div><p style={{fontSize:14, fontWeight:700, color:C.text}}>Accept new orders</p><p style={{fontSize:12, color:C.textMed}}>Pause without closing the store</p></div>
              <Toggle val={slotsOpen} onChange={setSlotsOpen}/>
            </div>
          </Card>
          <Card>
            <SHead title="Delivery hours"/>
            <div style={{display:'flex', flexDirection:'column', gap:10}}>
              {['Mon – Fri','Saturday','Sunday'].map((d,i)=>(
                <div key={i} style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:i<2?`1px solid ${C.border}`:'none'}}>
                  <span style={{fontSize:14, fontWeight:600, color:C.text}}>{d}</span>
                  <span style={{fontSize:13, color:C.textMed}}>6:00 AM – 9:00 PM</span>
                </div>
              ))}
            </div>
          </Card>
          <Card>
            <SHead title="Bank & payouts"/>
            {[{l:'Bank account',v:'HDFC ·· 4521'},{l:'UPI ID',v:'nandini@upi'},{l:'Next payout',v:'1 Jun 2026'}].map(([l,v]=[],i)=>(
              <div key={i} style={{display:'flex', justifyContent:'space-between', padding:'10px 0', borderBottom:i<2?`1px solid ${C.border}`:'none'}}>
                <span style={{fontSize:13, color:C.textMed}}>{l}</span>
                <span style={{fontSize:13, fontWeight:700, color:C.text}}>{v}</span>
              </div>
            ))}
          </Card>
          <Btn full outline onClick={goBack}>Switch to consumer mode</Btn>
        </>}

        {/* ── Invites ── */}
        {activeTab==='invites' && <>
          {/* KPI strip */}
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8}}>
            <div style={{padding:'12px', background:'#e6f7fb', borderRadius:14}}>
              <p style={{fontSize:18, fontWeight:800, color:C.primary}}>{invites.length}</p>
              <p style={{fontSize:10, fontWeight:600, color:C.textMed}}>Sent</p>
            </div>
            <div style={{padding:'12px', background:'#dcfce7', borderRadius:14}}>
              <p style={{fontSize:18, fontWeight:800, color:C.success}}>{paidInvites.length}</p>
              <p style={{fontSize:10, fontWeight:600, color:C.textMed}}>Paid · {conversionPct}%</p>
            </div>
            <div style={{padding:'12px', background:'#fef3c7', borderRadius:14}}>
              <p style={{fontSize:14, fontWeight:800, color:C.warning, fontVariantNumeric:'tabular-nums'}}>{fmt(invitesRevenueP)}</p>
              <p style={{fontSize:10, fontWeight:600, color:C.textMed}}>Revenue</p>
            </div>
          </div>

          {/* Send to customer form */}
          {!generated && (
            <Card>
              <SHead title="Send to a customer"/>
              <p style={{fontSize:11, color:C.textMed, marginBottom:12, lineHeight:1.5}}>Enter a phone number and what you'd like to sell. We'll generate a personalised link to share over WhatsApp.</p>

              <p style={{fontSize:11, fontWeight:800, color:C.textMed, marginBottom:6, letterSpacing:.4}}>CUSTOMER MOBILE</p>
              <div style={{display:'flex', gap:8, marginBottom:10}}>
                <div style={{padding:'10px 12px', borderRadius:10, border:`1px solid ${C.border}`, background:'#fafaf9', color:C.textMed, fontSize:13, fontWeight:700}}>+91</div>
                <input value={inviteForm.phone} onChange={(e)=>setInviteForm(f=>({...f, phone:e.target.value.replace(/\D/g,'').slice(0,10)}))} placeholder="10-digit number" inputMode="numeric"
                  style={{flex:1, padding:'10px 12px', borderRadius:10, border:`1.5px solid ${inviteForm.phone.length===10?C.primary:C.border}`, fontSize:14, color:C.text, outline:'none'}}/>
              </div>

              <p style={{fontSize:11, fontWeight:800, color:C.textMed, marginBottom:6, letterSpacing:.4}}>CUSTOMER NAME <span style={{fontWeight:400, color:C.textLight}}>(optional)</span></p>
              <input value={inviteForm.name} onChange={(e)=>setInviteForm(f=>({...f, name:e.target.value}))} placeholder="e.g. Priya"
                style={{width:'100%', padding:'10px 12px', borderRadius:10, border:`1.5px solid ${inviteForm.name?C.primary:C.border}`, fontSize:14, color:C.text, outline:'none', marginBottom:14}}/>

              <p style={{fontSize:11, fontWeight:800, color:C.textMed, marginBottom:8, letterSpacing:.4}}>WHAT WOULD YOU LIKE TO SELL?</p>
              <div style={{display:'flex', flexDirection:'column', gap:8, marginBottom:14}}>
                {[
                  {id:'store',   em:'🏬', l:'Store visit',         sub:'Customer lands on your store page'},
                  {id:'product', em:'📦', l:'Specific product',    sub:'Subscribe modal pre-opened on the product'},
                  {id:'bundle',  em:'📅', l:'Subscription bundle', sub:'Curate a multi-product routine for them'},
                ].map((o)=>{
                  const on = inviteForm.kind === o.id;
                  return (
                    <button key={o.id} onClick={()=>setInviteForm(f=>({...f, kind:o.id}))} style={{display:'flex', alignItems:'center', gap:10, padding:'10px 12px', borderRadius:12, border:`1.5px solid ${on?C.primary:C.border}`, background:on?'#e6f7fb':C.white, cursor:'pointer', textAlign:'left'}}>
                      <span style={{fontSize:22}}>{o.em}</span>
                      <div style={{flex:1, minWidth:0}}>
                        <p style={{fontSize:13, fontWeight:800, color:C.text}}>{o.l}</p>
                        <p style={{fontSize:11, color:C.textMed, marginTop:1}}>{o.sub}</p>
                      </div>
                      <div style={{width:18, height:18, borderRadius:9, border:`2px solid ${on?C.primary:'#d6d3d1'}`, display:'flex', alignItems:'center', justifyContent:'center', background:C.white, flexShrink:0}}>
                        {on && <div style={{width:9, height:9, borderRadius:5, background:C.primary}}/>}
                      </div>
                    </button>
                  );
                })}
              </div>

              {inviteForm.kind === 'product' && (
                <div style={{padding:12, background:C.bg, borderRadius:12, border:`1px solid ${C.border}`, marginBottom:14}}>
                  <p style={{fontSize:11, fontWeight:800, color:C.textMed, marginBottom:6, letterSpacing:.4}}>PRODUCT</p>
                  <select value={inviteForm.productId} onChange={(e)=>{const p=vendorProducts.find(x=>x.id===e.target.value); setInviteForm(f=>({...f, productId:e.target.value, variantLabel:p?.variants?.[0]?.l || ''}));}}
                    style={{width:'100%', padding:'10px 12px', borderRadius:10, border:`1.5px solid ${inviteForm.productId?C.primary:C.border}`, fontSize:13, color:C.text, background:C.white, outline:'none', marginBottom:8}}>
                    <option value="">Select a product…</option>
                    {vendorProducts.filter(p=>p.subscribable!==false).map(p=>(
                      <option key={p.id} value={p.id}>{p.em} {p.name}</option>
                    ))}
                  </select>
                  {inviteForm.productId && (
                    <>
                      <p style={{fontSize:11, fontWeight:800, color:C.textMed, marginBottom:6, marginTop:4, letterSpacing:.4}}>VARIANT</p>
                      <div style={{display:'flex', gap:6, flexWrap:'wrap'}}>
                        {(vendorProducts.find(x=>x.id===inviteForm.productId)?.variants || []).map(v=>{
                          const on = inviteForm.variantLabel === v.l;
                          return (
                            <button key={v.l} onClick={()=>setInviteForm(f=>({...f, variantLabel:v.l}))} style={{padding:'6px 10px', borderRadius:8, border:`1.5px solid ${on?C.primary:C.border}`, background:on?'#e6f7fb':C.white, color:on?C.primary:C.text, fontSize:12, fontWeight:700, cursor:'pointer'}}>{v.l} · {fmt(v.p)}</button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}

              {inviteForm.kind === 'bundle' && (
                <div style={{padding:12, background:C.bg, borderRadius:12, border:`1px solid ${C.border}`, marginBottom:14}}>
                  <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8}}>
                    <p style={{fontSize:11, fontWeight:800, color:C.textMed, letterSpacing:.4}}>BUNDLE ITEMS</p>
                    <button onClick={addBundleItem} style={{background:'none', border:'none', color:C.primary, fontSize:12, fontWeight:800, cursor:'pointer'}}>+ Add item</button>
                  </div>
                  {inviteForm.bundleItems.length === 0 && (
                    <p style={{fontSize:12, color:C.textMed, padding:'12px 0'}}>No items yet. Tap "+ Add item" to start curating.</p>
                  )}
                  {inviteForm.bundleItems.map((it, idx) => {
                    const product = vendorProducts.find(p => p.id === it.productId);
                    return (
                      <div key={idx} style={{display:'flex', flexDirection:'column', gap:6, padding:'10px 0', borderTop: idx>0 ? `1px solid ${C.border}` : 'none'}}>
                        <div style={{display:'flex', gap:6, alignItems:'center'}}>
                          <select value={it.productId} onChange={(e)=>{const p=vendorProducts.find(x=>x.id===e.target.value); updateBundleItem(idx, {productId:e.target.value, variantLabel:p?.variants?.[0]?.l || ''});}}
                            style={{flex:1, padding:'7px 8px', borderRadius:8, border:`1px solid ${C.border}`, fontSize:12, color:C.text, background:C.white, outline:'none'}}>
                            {vendorProducts.filter(p=>p.subscribable!==false).map(p=>(<option key={p.id} value={p.id}>{p.em} {p.name}</option>))}
                          </select>
                          <button onClick={()=>removeBundleItem(idx)} style={{width:28, height:28, borderRadius:8, border:`1px solid ${C.border}`, background:C.white, color:C.error, fontSize:14, cursor:'pointer'}}>×</button>
                        </div>
                        <div style={{display:'flex', gap:6, alignItems:'center', flexWrap:'wrap'}}>
                          {(product?.variants || []).map(v=>(
                            <button key={v.l} onClick={()=>updateBundleItem(idx, {variantLabel:v.l})} style={{padding:'4px 8px', borderRadius:6, border:`1px solid ${it.variantLabel===v.l?C.primary:C.border}`, background:it.variantLabel===v.l?'#e6f7fb':C.white, color:C.text, fontSize:11, fontWeight:700, cursor:'pointer'}}>{v.l}</button>
                          ))}
                          <span style={{fontSize:11, color:C.textMed}}>· qty</span>
                          <input type="number" min={1} max={20} value={it.qty} onChange={(e)=>updateBundleItem(idx, {qty:Math.max(1, parseInt(e.target.value)||1)})}
                            style={{width:48, padding:'4px 6px', borderRadius:6, border:`1px solid ${C.border}`, fontSize:11, textAlign:'center', outline:'none'}}/>
                          <select value={it.cadence} onChange={(e)=>updateBundleItem(idx, {cadence:e.target.value})}
                            style={{padding:'4px 6px', borderRadius:6, border:`1px solid ${C.border}`, fontSize:11, background:C.white, outline:'none'}}>
                            {(product?.cadences || ['Daily','Alt-day','Weekly','Custom']).map(c=>(<option key={c} value={c}>{c}</option>))}
                          </select>
                        </div>
                      </div>
                    );
                  })}
                  <div style={{marginTop:10, paddingTop:10, borderTop:`1px solid ${C.border}`}}>
                    <p style={{fontSize:11, fontWeight:800, color:C.textMed, marginBottom:6, letterSpacing:.4}}>DELIVERY WINDOW</p>
                    <div style={{display:'flex', gap:6, flexWrap:'wrap'}}>
                      {['6–8 AM','7–9 AM','8–10 AM','4–6 PM','6–8 PM'].map(w=>(
                        <button key={w} onClick={()=>setInviteForm(f=>({...f, deliveryWindow:w}))} style={{padding:'5px 10px', borderRadius:6, border:`1.5px solid ${inviteForm.deliveryWindow===w?C.primary:C.border}`, background:inviteForm.deliveryWindow===w?'#e6f7fb':C.white, color:C.text, fontSize:11, fontWeight:700, cursor:'pointer'}}>{w}</button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <Btn full disabled={!inviteFormValid()||creating} onClick={handleGenerate}>
                {creating ? 'Generating…' : 'Generate invite link'}
              </Btn>
            </Card>
          )}

          {/* Generated URL display */}
          {generated && (
            <Card style={{background:'#fff8f4', border:`1.5px solid ${C.primary}`}}>
              <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10}}>
                <p style={{fontSize:14, fontWeight:800, color:C.text}}>✓ Invite ready</p>
                <button onClick={handleResetForm} style={{background:'none', border:'none', color:C.textMed, fontSize:12, fontWeight:700, cursor:'pointer'}}>+ New</button>
              </div>
              <p style={{fontSize:11, color:C.textMed, marginBottom:8}}>Sent to <b style={{color:C.text}}>+91 {inviteForm.phone}</b>{inviteForm.name?` (${inviteForm.name})`:''}. Valid for 7 days.</p>
              <div style={{padding:'10px 12px', background:C.white, borderRadius:10, border:`1px dashed ${C.primary}`, marginBottom:10, fontSize:11, color:C.text, fontFamily:'monospace', wordBreak:'break-all'}}>
                {generated.url}
              </div>
              <div style={{display:'flex', gap:8}}>
                <button onClick={()=>handleCopy(generated.url)} style={{flex:1, padding:'10px 12px', borderRadius:10, border:`1.5px solid ${C.primary}`, background:copiedTimer?C.successBg:C.white, color:copiedTimer?C.success:C.primary, fontSize:13, fontWeight:800, cursor:'pointer'}}>
                  {copiedTimer ? '✓ Copied' : '📋 Copy link'}
                </button>
                <button onClick={()=>handleWhatsApp(generated.url, inviteForm.phone, inviteForm.name)} style={{flex:1, padding:'10px 12px', borderRadius:10, border:'none', background:'#25d366', color:C.white, fontSize:13, fontWeight:800, cursor:'pointer'}}>
                  📱 WhatsApp
                </button>
              </div>
            </Card>
          )}

          {/* Recent invites list */}
          <Card>
            <SHead title={`Recent invites (${invites.length})`}/>
            {invites.length === 0 ? (
              <p style={{fontSize:12, color:C.textMed, padding:'12px 0'}}>No invites yet. Use the form above to send your first one.</p>
            ) : invites.map((inv, i)=>(
              <div key={inv.token} style={{padding:'12px 0', borderTop: i>0 ? `1px solid ${C.border}` : 'none'}}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:10, marginBottom:6}}>
                  <div style={{minWidth:0, flex:1}}>
                    <p style={{fontSize:13, fontWeight:800, color:C.text, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{inv.customerName || 'Customer'} <span style={{fontWeight:500, color:C.textMed, fontSize:11}}>· {maskPhone(inv.customerPhone)}</span></p>
                    <p style={{fontSize:11, color:C.textMed, marginTop:2}}>{targetSummary(inv)}</p>
                  </div>
                  {statusPillFor(inv.status)}
                </div>
                <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:8}}>
                  <p style={{fontSize:10, color:C.textLight, fontVariantNumeric:'tabular-nums'}}>{new Date(inv.createdAt).toLocaleString('en-IN',{day:'numeric', month:'short', hour:'numeric', minute:'2-digit'})}{inv.revenueP?` · ${fmt(inv.revenueP)} earned`:''}</p>
                  {(inv.status === 'pending_otp' || inv.status === 'pending_payment') && (
                    <button onClick={()=>handleCancelInvite(inv.token)} style={{background:'none', border:'none', color:C.error, fontSize:11, fontWeight:700, cursor:'pointer'}}>Cancel</button>
                  )}
                </div>
              </div>
            ))}
          </Card>
        </>}

        <div style={{height:8}}/>
      </div>
    </div>
  );
};
